// src/core/services/apiService.js
import axios from "axios";
import { showNotification } from "./slices/notificationSlice"; // Adjust path as needed
import moment from "moment-timezone";

const baseURL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL,
  headers: {
    "x-api-key": process.env.REACT_APP_PUBLIC_API_KEY,
  },
  withCredentials: false, // ✅ set to true only if using cookies
});

// -------------------- Cache --------------------
const cache = {}; // { key: { timestamp, data } }

function getCacheKey(resource, params) {
  return `${resource}?${JSON.stringify(params || {})}`;
}

// Optional: cache expiry in ms (e.g., 5 minutes)
const CACHE_EXPIRY = 30 * 60 * 1000;

// -------------------- Helper functions --------------------
// ✅ Helper: recursively convert ISO dates to PH timezone
function convertDatesToPH(obj) {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertDatesToPH(item));
  } else if (obj && typeof obj === "object") {
    const newObj = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === "string" && isISODate(value)) {
        newObj[key] = moment.utc(value).tz("Asia/Manila").format();
      } else {
        newObj[key] = convertDatesToPH(value);
      }
    }
    return newObj;
  }
  return obj;
}

function isISODate(str) {
  if (typeof str !== "string") return false;

  // Matches ISO dates: YYYY-MM-DD or YYYY/MM/DD
  // optionally with time and timezone (T08:00:00Z or +08:00)
  const isoDateRegex =
    /^\d{4}([-/])\d{2}\1\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?)?$/;

  return isoDateRegex.test(str);
}

// -------------------- Axios interceptors --------------------
// ✅ Automatically convert GET response data to PH timezone
apiClient.interceptors.response.use(
  (response) => {
    if (response.config.method === "get" && response.data) {
      // console.log("before", response.data);
      response.data = convertDatesToPH(response.data);
      // console.log("after", response.data);
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// ✅ Interceptor to attach token automatically
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // 👈 store token after login
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// -------------------- Centralized handler --------------------
// Shared handler
const handleApiResponse = async (
  dispatch,
  apiCall,
  successMessage,
  errorMessage,
  showSuccess = true,
  triggerRefresh = false // ✅ new flag
) => {
  try {
    const response = await apiCall();
    if (showSuccess) {
      dispatch(
        showNotification({
          message: successMessage,
          type: "success",
        })
      );
    }

    // Dispatch refresh key only if needed
    if (triggerRefresh) {
      dispatch({ type: "SET_REFRESH_KEY", payload: Date.now() });
    }

    return response.data;
  } catch (error) {
    console.error(errorMessage, error);
    dispatch(
      showNotification({
        message: `${errorMessage}: ${
          error?.response?.data?.error || error.message
        }`,
        type: "error",
      })
    );
    throw error;
  }
};

function invalidateCache(resource) {
  // Remove all cache entries for the resource
  Object.keys(cache).forEach((key) => {
    if (key.startsWith(resource)) delete cache[key];
  });
}

// -------------------- API methods with caching --------------------
// API methods
const apiService = {
  get: async (dispatch, resource, query, useCache = true) => {
    const key = getCacheKey(resource, query);

    // ✅ Return cached data if valid
    if (
      useCache &&
      cache[key] &&
      Date.now() - cache[key].timestamp < CACHE_EXPIRY
    ) {
      return cache[key].data;
    }
    const data = await handleApiResponse(
      dispatch,
      () =>
        apiClient.get(`/${resource}`, {
          params: query, // 👈 this enables query string like ?role=doctor
        }),
      "",
      "Error fetching resource",
      false
    );

    // ✅ Save to cache
    if (useCache) {
      cache[key] = { timestamp: Date.now(), data };
    }

    return data;
  },

  put: async (dispatch, resource, id, data, isMultipart = false) => {
    const result = await handleApiResponse(
      dispatch,
      () =>
        apiClient.put(`/${resource}/${id}`, data, {
          headers: isMultipart ? { "Content-Type": "multipart/form-data" } : {},
        }),
      "Updated successfully",
      "Error updating resource",
      true,
      true
    );

    invalidateCache(resource); // ✅ remove old cache
    return result;
  },

  post: async (dispatch, resource, data, isMultipart = false) => {
    const result = await handleApiResponse(
      dispatch,
      () =>
        apiClient.post(`/${resource}`, data, {
          headers: isMultipart ? { "Content-Type": "multipart/form-data" } : {},
        }),
      "Created successfully",
      "Error creating resource",
      true,
      true
    );

    invalidateCache(resource); // ✅ remove old cache
    return result;
  },

  delete: async (dispatch, resource, id) => {
    const result = await handleApiResponse(
      dispatch,
      () => apiClient.delete(`/${resource}/${id}`),
      "Deleted successfully",
      "Error deleting resource",
      true,
      true
    );

    invalidateCache(resource); // ✅ remove old cache
    return result;
  },
};

export default apiService;
